import { AlertCircle, ArrowLeft, ShieldCheck, Sparkles } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button } from '../components/Button';
import { PinBoxes, PIN_LENGTH, suggestPin } from '../components/inputs/PinBoxes';
import { authApi } from '../features/auth/authApi';
import type { AuthResponse, PinChallengeIssued } from '../features/auth/authTypes';
import { ApiError } from '../lib/apiError';

/**
 * The second leg of the sign-in: the PIN, choosing one, or replacing one an administrator issued.
 *
 * **Replacing is ONE leg, not two.** The issued PIN and its replacement go up together, so the
 * PIN an administrator has seen never on its own buys a session — the server refuses it
 * without a replacement, whatever this form sends.
 *
 * **It never holds the password and never retries the first leg.** What it holds is the
 * ticket, which is the only thing binding it to an account — so Back throws the ticket away
 * and returns to a blank password field rather than resubmitting anything.
 */
export function SignInPinStep({
  challenge,
  onSignedIn,
  onBack,
}: {
  challenge: PinChallengeIssued;
  onSignedIn: (session: AuthResponse) => void;
  onBack: () => void;
}) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const enrolling = challenge.requiresEnrolment;
  // Never both: an account with no PIN has nothing issued to replace.
  const changing = !enrolling && challenge.requiresChange;
  const ready = enrolling
    ? pin.length === PIN_LENGTH && confirmPin.length === PIN_LENGTH
    : changing
      ? pin.length === PIN_LENGTH &&
        newPin.length === PIN_LENGTH &&
        confirmNewPin.length === PIN_LENGTH
      : pin.length === PIN_LENGTH;

  async function submit() {
    if (!ready || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const session = enrolling
        ? await authApi.enrolPin({ ticket: challenge.ticket, pin, confirmPin })
        : await authApi.verifyPin(
            changing
              ? { ticket: challenge.ticket, pin, newPin, confirmNewPin }
              : { ticket: challenge.ticket, pin },
          );

      onSignedIn(session);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not reach the server. Check your connection and try again.', 0),
      );

      // Clear the boxes so the next attempt starts from an empty control rather than from six
      // characters the reader has to delete one at a time.
      setPin('');
      setConfirmPin('');
      setNewPin('');
      setConfirmNewPin('');
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit();
  }

  return (
    <div className="flex flex-col gap-5">
      {error ? (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-xs text-red-700">{error.message}</p>
            {error.correlationId ? (
              <p className="mt-1 font-mono tabular-nums text-[11px] text-red-500">
                Reference {error.correlationId}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col items-center gap-1 text-center">
        <ShieldCheck className="h-5 w-5 text-brand-700" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-800">
          {enrolling
            ? 'Choose your sign-in PIN'
            : changing
              ? 'Replace the PIN you were given'
              : 'Enter your sign-in PIN'}
        </p>
        <p id="signin-pin-help" className="text-xs text-slate-500">
          {enrolling
            ? 'Six characters, letters and numbers. You will enter this every time you sign in, so pick something you will remember without writing down.'
            : changing
              ? 'An administrator set this PIN, so it must be replaced. Enter it, then choose your own.'
              : 'Six characters, letters and numbers.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <PinBoxes
          id="signin-pin"
          label={enrolling ? 'New sign-in PIN' : changing ? 'The PIN you were given' : 'Sign-in PIN'}
          describedBy="signin-pin-help"
          value={pin}
          onChange={setPin}
          // Entering an existing PIN submits as soon as the last box is filled — there is
          // nothing else on the form to fill in, so asking for a second gesture is asking twice.
          // ENROLLING does not, because the confirmation box is still empty.
          // CHANGING does not either: the new PIN is still to come.
          onComplete={enrolling || changing ? undefined : () => void submit()}
          disabled={submitting}
          invalid={error !== null}
          autoFocus
        />

        {enrolling ? (
          <div className="flex flex-col gap-2">
            {/*
              The generator, and it fills BOTH boxes.

              Filling only the first would leave the reader copying six characters from one row
              of boxes into another to confirm a value they never chose — a confirmation step
              that confirms nothing but their transcription. The point of the second row is to
              catch a TYPO, and a suggested PIN was not typed.
            */}
            {/*
              NOT `Wand2`, which is reserved for the Autogenerate button in `Modal.tsx` and must
              appear nowhere else — this page is not a modal and has no form contract to
              satisfy. (Spelling that component name in angle brackets here is what made
              `test:conventions` read this file as a modal and demand its button say "Submit".)
              And it is a
              `<Button>` rather than a bare styled `<button>`: hand-rolled, it measured 25px,
              under reference §5's 40px floor, which only MEASURING the rendered box showed.
            */}
            <Button
              id="signin-pin-suggest"
              type="button"
              variant="tertiary"
              leading={<Sparkles className="h-4 w-4" aria-hidden="true" />}
              disabled={submitting}
              onClick={() => {
                const suggested = suggestPin();
                setPin(suggested);
                setConfirmPin(suggested);
                setError(null);
              }}
              className="mx-auto"
            >
              Suggest a PIN
            </Button>

            <p id="signin-pin-confirm-help" className="text-center text-xs text-slate-500">
              Enter it again to confirm
            </p>
            <PinBoxes
              id="signin-pin-confirm"
              label="Confirm your new sign-in PIN"
              describedBy="signin-pin-confirm-help"
              value={confirmPin}
              onChange={setConfirmPin}
              onComplete={() => void submit()}
              disabled={submitting}
              invalid={error !== null}
              // Not a one-time code: offering a password manager the same value twice is how a
              // confirmation box gets filled without anybody confirming anything.
              autoComplete="off"
            />
          </div>
        ) : null}

        {changing ? (
          <div className="flex flex-col gap-2">
            <p id="signin-pin-new-help" className="text-center text-xs text-slate-500">
              Choose your new PIN
            </p>
            <PinBoxes
              id="signin-pin-new"
              label="New sign-in PIN"
              describedBy="signin-pin-new-help"
              value={newPin}
              onChange={setNewPin}
              disabled={submitting}
              invalid={error !== null}
              autoComplete="off"
            />

            {/* Fills both new rows and leaves the issued PIN alone — see the enrolment block. */}
            <Button
              id="signin-pin-new-suggest"
              type="button"
              variant="tertiary"
              leading={<Sparkles className="h-4 w-4" aria-hidden="true" />}
              disabled={submitting}
              onClick={() => {
                const suggested = suggestPin();
                setNewPin(suggested);
                setConfirmNewPin(suggested);
                setError(null);
              }}
              className="mx-auto"
            >
              Suggest a PIN
            </Button>

            <p id="signin-pin-new-confirm-help" className="text-center text-xs text-slate-500">
              Enter it again to confirm
            </p>
            <PinBoxes
              id="signin-pin-new-confirm"
              label="Confirm your new sign-in PIN"
              describedBy="signin-pin-new-confirm-help"
              value={confirmNewPin}
              onChange={setConfirmNewPin}
              onComplete={() => void submit()}
              disabled={submitting}
              invalid={error !== null}
              autoComplete="off"
            />
          </div>
        ) : null}

        <Button
          id="signin-pin-submit"
          type="submit"
          variant="primary"
          fullWidth
          disabled={!ready}
          loading={submitting}
          loadingLabel={enrolling || changing ? 'Saving' : 'Signing in'}
          className="mt-1"
        >
          {enrolling || changing ? 'Save and sign in' : 'Sign in'}
        </Button>
      </form>

      <Button
        id="signin-pin-back"
        type="button"
        variant="tertiary"
        leading={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
        onClick={onBack}
        className="mx-auto"
      >
        Back to sign in
      </Button>
    </div>
  );
}
